/*
  Infinite scroll for paginated product grids.

  Progressive enhancement over `snippets/infinite-scroll.liquid`: the markup ships a real
  <a href="{next page}"> so crawlers and no-JS visitors still get paginated pages. When JS
  runs, a sentinel below the grid auto-fetches the next page via the Section Rendering API
  and appends the cards to the existing grid.
*/
class ProductInfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.grid = this.closest('.collection')?.querySelector('#product-grid') || document.getElementById('product-grid');
    this.button = this.querySelector('[data-infinite-scroll-button]');
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.status = this.querySelector('[data-infinite-scroll-status]');
    this.loading = false;

    this.button?.addEventListener('click', this.onButtonClick.bind(this));

    if (this.dataset.auto === 'true' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onIntersection.bind(this), { rootMargin: '600px 0px' });
      this.observer.observe(this.sentinel);
      this.classList.add('infinite-scroll--enhanced');
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  onIntersection(entries) {
    if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
  }

  onButtonClick(event) {
    // Let the link behave as a plain link when we cannot enhance it (no next url left).
    if (!this.dataset.nextUrl) return;
    event.preventDefault();
    this.loadNextPage();
  }

  async loadNextPage() {
    if (this.loading || !this.dataset.nextUrl || !this.grid) return;

    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    this.classList.add('infinite-scroll--loading');
    if (this.status) this.status.textContent = 'Loading more products…';

    try {
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelectorAll('#product-grid > li');
      if (items.length === 0) throw new Error('No products found on the next page');

      items.forEach((item) => this.grid.appendChild(document.importNode(item, true)));
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(this.grid);

      const nextPage = html.querySelector('product-infinite-scroll');
      const nextUrl = nextPage?.dataset.nextUrl;

      if (nextUrl) {
        this.dataset.nextUrl = nextUrl;
        if (this.button) this.button.href = nextUrl;
      } else {
        this.finish();
      }

      if (this.status) this.status.textContent = `${items.length} more products loaded.`;
    } catch (error) {
      console.error(error);
      // Fall back to the plain paginated link so the visitor is never stuck.
      this.classList.remove('infinite-scroll--enhanced');
      this.observer?.disconnect();
      this.dataset.nextUrl = '';
      if (this.status) this.status.textContent = 'Could not load more products.';
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      this.classList.remove('infinite-scroll--loading');
    }
  }

  finish() {
    this.dataset.nextUrl = '';
    this.observer?.disconnect();
    this.button?.remove();
    this.sentinel?.remove();
    this.classList.add('infinite-scroll--complete');
  }
}

customElements.define('product-infinite-scroll', ProductInfiniteScroll);
